import { QueryClient, useQuery } from "@tanstack/react-query";
import { QuestionResultsFilterLabels, QuestionResultsFilters } from "types";
import { isNullOrUndefOrEmpty, noOp, transformPercent } from "@civicscience/chops";

import { QuestionsAPI } from "api";
import { cacheKeyForDateRange } from "utils";

type QuestionResult = {
  answerChoiceId: number;
  label: string;
  order: number;
  count: number;
  pct: number;
};

type QuestionResults = {
  questionId: number;
  label: string;
  results: QuestionResult[];
  totalCount: number;
  errorMargin: number;
  startTime: Date | null;
  endTime: Date;
  filters: QuestionResultsFilterLabels;
};

type UseQuestionResultsArgs = {
  questionId: number | string;
  filters: QuestionResultsFilters;
  valueGroupingUri: string | null;
  enableFetch?: boolean;
  onError?: (err: unknown) => void;
};

/**
 * Transforms the `pct` of each result to be in the expected format for charting.
 */
const transformQuestionResults = (response: QuestionResults) => ({
  ...response,
  results: response.results.map((result) => ({
    ...result,
    pct: transformPercent(result.pct),
  })),
});

/**
 * Provides a query to fetch QuestionResults data.
 * This is extracted so that the QuestionResultsDashlet can share the same query
 * as the QuestionResultsChart component.
 */
const useQuestionResults = ({
  questionId,
  filters,
  valueGroupingUri,
  enableFetch = true,
  onError = noOp,
}: UseQuestionResultsArgs) => {
  const { startDate, endDate, ...restFilters } = filters;
  const cacheKey = isNullOrUndefOrEmpty(valueGroupingUri)
    ? "question-results-chart"
    : "question-value-grouping-results-chart";
  const endpoint = isNullOrUndefOrEmpty(valueGroupingUri)
    ? QuestionsAPI.getResults
    : QuestionsAPI.getValueGroupingResults;
  return useQuery(
    [cacheKey, { questionId }, restFilters, cacheKeyForDateRange({ startDate, endDate }), valueGroupingUri],
    () => endpoint({ questionId, ...filters, valueGroupingUri }),
    {
      select: transformQuestionResults,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 3,
      enabled: enableFetch,
      onError,
    },
  );
};

/**
 * Invalidates the question results query for a given questionId.
 */
export const invalidateQuestionResults = (queryClient: QueryClient, questionId: number) => {
  queryClient.invalidateQueries(["question-results-chart", { questionId: questionId }]);
  queryClient.invalidateQueries(["question-value-grouping-results-chart", { questionId }]);
};

export default useQuestionResults;
